import {
  Bell,
  Building2,
  ChevronLeft,
  LayoutDashboard,
  LogOut,
  Menu,
  MonitorUp,
  RadioTower,
  Settings2,
  ShieldCheck,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useSession } from "../auth/SessionContext";
import { readStoredSidebarCollapsed, storeSidebarCollapsed } from "../lib/sidebar-settings";
import { IconButton } from "./ui";
import { ThemeToggle } from "./ThemeToggle";

const mainNav = [
  { to: "/", label: "Resumen", icon: LayoutDashboard, end: true },
  { to: "/problemas", label: "Problemas", icon: Bell },
  { to: "/edificios", label: "Edificios", icon: Building2 },
  { to: "/mural", label: "Modo mural", icon: MonitorUp },
];

const adminNav = [
  { to: "/administracion/conectores", label: "Conectores", icon: RadioTower },
  { to: "/administracion/notificaciones", label: "Notificaciones", icon: Settings2 },
];

function NavItem({ item, onNavigate }: { item: (typeof mainNav)[number]; onNavigate: () => void }) {
  const Icon = item.icon;
  const endProps = "end" in item && item.end ? { end: true } : {};
  return (
    <NavLink to={item.to} {...endProps} className={({ isActive }) => `nav-link${isActive ? " nav-link--active" : ""}`} onClick={onNavigate}>
      <Icon size={19} aria-hidden="true" />
      <span>{item.label}</span>
    </NavLink>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { role, logout } = useSession();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => readStoredSidebarCollapsed(window.localStorage));
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => storeSidebarCollapsed(window.localStorage, collapsed), [collapsed]);
  useEffect(() => {
    if (!open || !window.matchMedia("(max-width: 1040px)").matches) return;
    closeButtonRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  return (
    <div className={`app-frame${collapsed ? " app-frame--collapsed" : ""}`}>
      <a className="skip-link" href="#main-content">Saltar al contenido</a>
      <header className="mobile-header">
        <button className="brand brand--mobile" onClick={() => setOpen(true)} aria-label="Abrir menú" aria-expanded={open} aria-controls="primary-sidebar">
          <span className="brand__mark"><ShieldCheck size={21} aria-hidden="true" /></span>
          <span>Centro Monitoreo Hikdos</span>
        </button>
        <IconButton label="Abrir navegación" onClick={() => setOpen(true)} aria-expanded={open} aria-controls="primary-sidebar"><Menu size={21} /></IconButton>
      </header>
      {open ? <button className="sidebar-backdrop" aria-label="Cerrar menú" onClick={() => setOpen(false)} /> : null}
      <aside id="primary-sidebar" className={`sidebar${open ? " sidebar--open" : ""}`} aria-label="Navegación principal">
        <div className="sidebar__top">
          <div className="brand">
            <span className="brand__mark"><ShieldCheck size={22} aria-hidden="true" /></span>
            <span className="brand__copy"><strong>Centro Monitoreo</strong><small>Hikdos</small></span>
          </div>
          <IconButton ref={closeButtonRef} label="Cerrar menú" className="sidebar__close" onClick={() => setOpen(false)}><X size={20} /></IconButton>
        </div>
        <nav className="sidebar__nav">
          <p className="nav-section-label">Operación</p>
          {mainNav.map((item) => <NavItem key={item.to} item={item} onNavigate={() => setOpen(false)} />)}
          {role === "admin" ? (
            <>
              <p className="nav-section-label">Administración</p>
              {adminNav.map((item) => <NavItem key={item.to} item={item} onNavigate={() => setOpen(false)} />)}
            </>
          ) : null}
        </nav>
        <div className="sidebar__footer">
          <ThemeToggle />
          <div className="role-card">
            <span className="role-card__icon"><ShieldCheck size={18} aria-hidden="true" /></span>
            <span className="role-card__copy"><small>Sesión actual</small><strong>{role === "admin" ? "Administrador" : "Operador"}</strong></span>
          </div>
          <button className="nav-link" onClick={() => void logout()}><LogOut size={19} aria-hidden="true" /><span>Cerrar sesión</span></button>
          <button className="sidebar__collapse" onClick={() => setCollapsed((value) => !value)} aria-expanded={!collapsed} aria-label={collapsed ? "Expandir menú" : "Contraer menú"}>
            <ChevronLeft size={17} aria-hidden="true" />
            <span>Contraer menú</span>
          </button>
        </div>
      </aside>
      <main id="main-content" className="main-content" tabIndex={-1}>{children}</main>
    </div>
  );
}
